import {
  lazy,
  Suspense,
  useEffect, useId, useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  Clock3,
  ExternalLink,
  ListChecks,
  Play,
  RotateCcw,
  ShieldCheck,
  X,
} from "lucide-react";
import { useStudioStore } from "@/store/studio-store";
import { useStudioLocale } from "@/i18n";
import {
  getStudioExecutionCopy,
  resolveStudioExecutionErrorCopy,
  type ResolvedStudioExecutionErrorCopy,
} from "@/i18n/studio-execution-copy";
import { getStudioAuthorityTaskCopy } from "@/i18n/studio-authority-copy";
import { mcpBus } from "@/mcp/studio-mcp-bus";
import { useIntentPreview } from "@/features/agent/hooks/use-intent-preview";
import {
  getStudioCapabilityConfirmationLifecycle,
  getStudioCapabilityGatewayRevision,
  hydrateStudioCapabilityExecutionsFromDurableJournal,
  listPendingStudioCapabilities,
  listStudioCapabilityExecutions,
  subscribeStudioCapabilityGateway,
  cancelStudioCapability,
  confirmStudioCapability,
  type StudioCapabilityChangePreview,
  type StudioCapabilityFailureCode,
  undoStudioCapabilityExecution,
} from "@/features/chat/services/studio-capability-gateway";
import { buildStudioCommandDeckSnapshot } from "@/features/chat/services/studio-command-deck";
import {
  buildStudioTaskSnapshot,
  type StudioTaskPhase,
} from "@/features/chat/services/studio-task-orchestrator";
import {
  activateStudioGoldenJourneyStep,
  clearStudioGoldenJourney,
  getActiveStudioGoldenJourneySnapshot,
  getStudioGoldenJourneyRevision,
  subscribeStudioGoldenJourney,
  type StudioGoldenJourneySnapshot,
} from '@/features/chat/services/studio-golden-journeys'
import { useStudioConversationSurfaceLabel, useStudioProjectOutputKind } from './artifact-mode-switcher'
import { findStudioAuthorityTask } from '../studio-authority-workspaces'
import { getCoogoAsIsArtifactLabel } from '../coogo-ide-renewal-copy'
import { useAsIsSourceMarkupPresent } from '@/features/preview/hooks/use-as-is-source-markup'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'

const LazyStudioExecutionTimeline = lazy(() =>
  import("./studio-execution-timeline").then((module) => ({
    default: module.StudioExecutionTimeline,
  })),
);

const PHASE_ICON = {
  understanding: CircleDashed,
  "needs-input": AlertCircle,
  ready: CircleDashed,
  "awaiting-confirmation": ShieldCheck,
  running: Clock3,
  applied: CheckCircle2,
  verified: ShieldCheck,
  blocked: AlertCircle,
  failed: AlertCircle,
  undone: RotateCcw,
} satisfies Record<StudioTaskPhase, typeof CircleDashed>;

const PHASE_TONE: Record<StudioTaskPhase, string> = {
  understanding: "text-muted-foreground",
  "needs-input": "text-amber-700 dark:text-amber-300",
  ready: "text-foreground",
  "awaiting-confirmation": "text-amber-700 dark:text-amber-300",
  running: "text-primary",
  applied: "text-foreground",
  verified: "text-success",
  blocked: "text-destructive",
  failed: "text-destructive",
  undone: "text-muted-foreground",
};

function formatImpactValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function localizedApprovalFailure(
  copy: ReturnType<typeof getStudioExecutionCopy>["commandDeck"],
  code: StudioCapabilityFailureCode | undefined,
): string {
  return code ? copy.confirmationFailure[code] : copy.approvalFailed;
}

export function StudioTaskCenter() {
  const executionDetailsId = useId()
  const { locale, strings } = useStudioLocale();
  const executionCopy = getStudioExecutionCopy(locale);
  const [executionDetailsOpen, setExecutionDetailsOpen] = useState(false);
  const [impactOpen, setImpactOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [approvalState, setApprovalState] = useState<
    "idle" | "applying" | "cancelling"
  >("idle");
  const [approvalOutcome, setApprovalOutcome] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [undoError, setUndoError] =
    useState<ResolvedStudioExecutionErrorCopy | null>(null);
  useSyncExternalStore(
    subscribeStudioCapabilityGateway,
    getStudioCapabilityGatewayRevision,
    getStudioCapabilityGatewayRevision,
  );
  const studioPersona = useStudioStore((state) => state.studioPersona);
  const activeSurface = useStudioStore((state) => state.activeRailItem);
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const accessToken = useStudioStore(
    (state) => state.session?.access_token ?? null,
  );
  const principalId = useStudioStore((state) => state.user?.id ?? null);
  const projectPlan = useStudioStore((state) => state.projectPlan);
  const projectSettings = useStudioStore((state) => state.projectSettings);
  // fix-round-2 — the SAME predicate the tab (artifact-mode-switcher.tsx)
  // and the pane (as-is-view.tsx) derive from, so this rail's title +
  // description can never independently drift from what they say about the
  // same snapshot. fix-round-4 routes it through the shared hook so there is
  // one decision point rather than one store read per surface.
  const asIsSourceMarkupPresent = useAsIsSourceMarkupPresent();
  const activeSurfaceLabel = useStudioConversationSurfaceLabel(activeSurface);
  const outputKind = useStudioProjectOutputKind();
  const context = useStudioStore((state) => state.chatContext);
  const dispatchStatus = useStudioStore((state) => state.channelDispatchStatus)
  // The OTHER queue this Studio asks for decisions through. A generated
  // application arrives here, not in the capability gateway's `pending`, so a
  // task centre that read only `pending` reported "요청 이해 중" while the
  // application it had just produced sat on screen with 승인 / 거절 beside it.
  const intentPreview = useIntentPreview()
  useSyncExternalStore(
    subscribeStudioGoldenJourney,
    getStudioGoldenJourneyRevision,
    getStudioGoldenJourneyRevision,
  );
  const pending = listPendingStudioCapabilities();
  const executions = listStudioCapabilityExecutions();

  useEffect(() => {
    if (!currentProjectId || !accessToken) return;
    void hydrateStudioCapabilityExecutionsFromDurableJournal().catch(() => {
      // Durable readback is additive. Keep the current session journal visible
      // when the remote list request is unavailable, and avoid leaking an
      // unhandled rejection from this best-effort hydration boundary.
    });
  }, [accessToken, currentProjectId, principalId]);

  const task = buildStudioTaskSnapshot({
    activeSurface,
    activeSurfaceLabel,
    outputKind,
    context,
    dispatchStatus,
    pending,
    executions,
    awaitingDecision: Boolean(intentPreview && !intentPreview.resolved),
    locale,
  })
  const pendingConfirmationId = task.pending?.confirmationId ?? null
  const activeConfirmationIdRef = useRef(pendingConfirmationId)
  const confirmationId = pendingConfirmationId ?? activeConfirmationIdRef.current
  const confirmationLifecycle = confirmationId ? getStudioCapabilityConfirmationLifecycle(confirmationId) : null
  const pendingForApproval = confirmationLifecycle?.status === 'executing' ? null : task.pending
  const lifecyclePhase: StudioTaskPhase | null = confirmationLifecycle
    ? confirmationLifecycle.status === "executing"
      ? "running"
      : confirmationLifecycle.status === "executed"
        ? task.phase
        : confirmationLifecycle.status === "cancelled"
          ? "blocked"
          : confirmationLifecycle.status === "failed"
            ? "failed"
            : null
    : null;
  const effectivePhase = lifecyclePhase ?? task.phase;
  const commandDeck = buildStudioCommandDeckSnapshot({
    task:
      pendingForApproval === task.pending
        ? task
        : { ...task, pending: pendingForApproval },
    projectIntent: projectPlan?.intent,
  });
  const authorityTask = task.authorityDomain
    ? findStudioAuthorityTask(
        task.authorityDomain,
        task.authorityView ?? undefined,
        outputKind,
      )
    : null;
  const authorityTaskCopy =
    task.authorityDomain && authorityTask
      ? getStudioAuthorityTaskCopy(
          locale,
          task.authorityDomain,
          outputKind,
          authorityTask,
          asIsSourceMarkupPresent,
        )
      : null;
  const localizedAuthorityTitle =
    task.authorityDomain && authorityTaskCopy
      ? `${
          task.authorityDomain === "primary"
            ? strings.artifacts[outputKind]
            // "as-is" has no artifacts-dictionary entry — same dedicated
            // label function the mode switcher uses.
            : task.authorityDomain === "as-is"
              ? getCoogoAsIsArtifactLabel(locale, asIsSourceMarkupPresent)
              : strings.artifacts[task.authorityDomain]
        } · ${authorityTaskCopy.label}`
      : task.surfaceLabel;
  const useLocalizedAuthorityGuidance =
    effectivePhase === "understanding" && Boolean(authorityTaskCopy);
  // `course.running` describes a CONFIRMED capability execution ("applying the
  // approved plan, verifying by readback"). A chat request in flight was
  // approved by nobody and may come back as prose, so it gets the course that
  // says what is actually happening. One site, keyed on the same status the
  // phase was derived from.
  const courseCopy =
    effectivePhase === "running" &&
    dispatchStatus.phase === "request-in-flight"
      ? executionCopy.commandDeck.courseRequestInFlight
      : executionCopy.commandDeck.course[effectivePhase];
  const localizedCourseMessage = useLocalizedAuthorityGuidance
    ? (authorityTaskCopy?.description ?? courseCopy.message)
    : courseCopy.message;
  // C5 — the developer-facing "applied" copy names an internal artifact
  // ("...review the execution evidence."); the creator persona gets a
  // plain-language alternative instead. Only the "applied" phase is
  // covered per the audit finding.
  const useCreatorAppliedNextAction =
    effectivePhase === "applied" && studioPersona !== "developer";
  const localizedCourseNextAction = useLocalizedAuthorityGuidance
    ? executionCopy.commandDeck.describeNext
    : useCreatorAppliedNextAction
      ? executionCopy.commandDeck.appliedNextActionCreator
      : courseCopy.nextAction;
  const journey = getActiveStudioGoldenJourneySnapshot({
    pending,
    executions,
  });
  const Icon = PHASE_ICON[effectivePhase];
  const approvalOutcomeRef = useRef<HTMLDivElement>(null);
  const lifecycleOutcome =
    confirmationLifecycle?.status === "failed"
      ? {
          kind: "error" as const,
          message: localizedApprovalFailure(
            executionCopy.commandDeck,
            confirmationLifecycle.failureCode,
          ),
        }
      : confirmationLifecycle?.status === "cancelled"
        ? {
            kind: "success" as const,
            message: executionCopy.commandDeck.approvalCancelled,
          }
        : null;
  const visibleApprovalOutcome = approvalOutcome ?? lifecycleOutcome;

  useEffect(() => {
    if (
      !pendingConfirmationId ||
      pendingConfirmationId === activeConfirmationIdRef.current
    )
      return;
    activeConfirmationIdRef.current = pendingConfirmationId;
    setConfirmationText("");
    setApprovalState("idle");
    setApprovalOutcome(null);
  }, [pendingConfirmationId]);

  useEffect(() => {
    if (!visibleApprovalOutcome) return;
    approvalOutcomeRef.current?.focus();
  }, [visibleApprovalOutcome]);

  const artifactLabel = (
    domain: (typeof commandDeck.affectedArtifacts)[number]["domain"],
  ) =>
    domain === "primary"
      ? strings.artifacts[outputKind]
      // "as-is" is never actually produced here (studio-command-deck.ts's
      // classifier excludes it — read-only, nothing writes into it — see
      // ImpactableDomain there), but the field's declared type is the full
      // StudioAuthorityDomain, so this stays exhaustive rather than assume
      // that invariant from a distance.
      //
      // FINAL-REVIEW M3 — and being exhaustive is not enough: this call had
      // no `asIsSourceMarkupPresent`, seventy lines below the rail title
      // (line ~229) that passes it. Unreachable today, but the day "as-is"
      // becomes impactable the chip would have said "Original structure"
      // while every other surface said "Original" — the fifth escape of this
      // exact class, each one onto a surface the previous round did not know
      // existed. Same flag, same answer, no second decision.
      : domain === "as-is"
        ? getCoogoAsIsArtifactLabel(locale, asIsSourceMarkupPresent)
        : strings.artifacts[domain];

  const openSurface = async () => {
    await mcpBus.execute({
      tool: "cogo.studio.openIdeSurface",
      params: { surface: task.surface, keepChatOpen: true },
    });
  };

  const undoExecution = async () => {
    if (!task.execution || isUndoing) return;
    setIsUndoing(true);
    setUndoError(null);
    try {
      const result = await undoStudioCapabilityExecution(
        task.execution.executionId,
      );
      if (!result.undone) {
        // final-review I5, wave-1 fix-round-2 — three of
        // undoStudioCapabilityExecution's non-throwing failure branches now
        // carry a `reason` (not a CoogoExecutionError `.code`; still out of
        // task-1's literal scope, see task-1-report.md) that resolves
        // through locale copy, so the primary line customers see is never
        // the raw English `result.error` literal on that path. The raw
        // string stays reachable as secondary `detail` (constraint 6: never
        // lose information). The 4th non-throwing failure (the MCP bus call
        // itself failing) has no `reason` and keeps the pre-existing
        // fallback to `result.error ?? restoreFailure` — that message comes
        // from the bus/tool layer, not one of the three fixed literals this
        // fix covers.
        setUndoError({
          primary: result.reason
            ? executionCopy.taskCenter.undoFailureReasons[result.reason]
            : (result.error ?? executionCopy.taskCenter.restoreFailure),
          ...(result.error ? { detail: result.error } : {}),
        });
      }
    } catch (error) {
      // task-1 (studio-ko-quality-repair-wave-1), fix-round-1 (C1) — a thrown
      // CoogoExecutionError (e.g. an execution-lease conflict) must resolve
      // to locale copy AND keep its code/message reachable as secondary
      // detail (constraint 6) — never collapse to .primary alone, or the
      // detail becomes unreachable to the customer.
      setUndoError(resolveStudioExecutionErrorCopy(executionCopy, error));
    } finally {
      setIsUndoing(false);
    }
  };

  const approvePendingPlan = async () => {
    if (
      !pendingForApproval ||
      !commandDeck.approvalRequired ||
      approvalState !== "idle"
    )
      return;
    setApprovalState("applying");
    setApprovalOutcome(null);
    try {
      const result = await confirmStudioCapability(
        pendingForApproval.confirmationId,
        undefined,
        confirmationText,
      );
      if (result.status !== "executed") {
        setApprovalOutcome({
          kind: "error",
          message: localizedApprovalFailure(
            executionCopy.commandDeck,
            result.status === "failed" ? result.code : undefined,
          ),
        });
        return;
      }
      setImpactOpen(false);
      setApprovalOutcome({
        kind: "success",
        message: executionCopy.commandDeck.approvalApplied,
      });
    } catch {
      setApprovalOutcome({
        kind: "error",
        message: localizedApprovalFailure(executionCopy.commandDeck, undefined),
      });
    } finally {
      setApprovalState("idle");
    }
  };

  const cancelPendingPlan = async () => {
    if (!pendingForApproval || approvalState !== "idle") return;
    setApprovalState("cancelling");
    setApprovalOutcome(null);
    try {
      const cancelled = await cancelStudioCapability(
        pendingForApproval.confirmationId,
      );
      if (!cancelled) {
        setApprovalOutcome({
          kind: "error",
          message: executionCopy.commandDeck.cancellationFailed,
        });
        return;
      }
      setImpactOpen(false);
      setApprovalOutcome({
        kind: "success",
        message: executionCopy.commandDeck.approvalCancelled,
      });
    } catch {
      setApprovalOutcome({
        kind: "error",
        message: executionCopy.commandDeck.cancellationFailed,
      });
    } finally {
      setApprovalState("idle");
    }
  };

  const impactChangesByPath = new Map<string, StudioCapabilityChangePreview>();
  for (const change of pendingForApproval?.impact.changes ?? []) {
    if (change.path.trim()) impactChangesByPath.set(change.path, change);
  }
  for (const path of pendingForApproval?.changedPaths ??
    task.execution?.changedPaths ??
    []) {
    if (path.trim() && !impactChangesByPath.has(path)) {
      impactChangesByPath.set(path, { path, label: path });
    }
  }
  const impactChanges = [...impactChangesByPath.values()];
  const typedApprovalReady =
    pendingForApproval?.impact.confirmation !== "typed" ||
    confirmationText.trim() === pendingForApproval.impact.confirmationPhrase;

  return (
    <section
      data-testid="studio-task-center"
      data-task-phase={effectivePhase}
      className="border-b border-border bg-muted/20 px-4 py-3"
      aria-live="polite"
    >
      <div data-testid="studio-command-deck-goal">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {executionCopy.commandDeck.goal}
        </span>
        <p
          className={`mt-1 text-sm font-semibold leading-5 ${
            commandDeck.goal ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {commandDeck.goal ?? executionCopy.commandDeck.goalMissing}
        </p>
      </div>

      <div className="mt-3 border-t border-border/70 pt-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {executionCopy.commandDeck.currentCourse}
          </span>
          <span
            data-testid="studio-task-phase"
            className={`shrink-0 text-[11px] font-medium ${PHASE_TONE[effectivePhase]}`}
          >
            {executionCopy.commandDeck.phase[effectivePhase]}
          </span>
        </div>
        <div className="mt-2 flex min-w-0 items-start gap-2.5">
          <Icon
            className={`mt-0.5 h-4 w-4 shrink-0 ${PHASE_TONE[effectivePhase]}`}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold text-foreground">
              {localizedAuthorityTitle}
            </span>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              {localizedCourseMessage}
            </p>
            <div className="mt-1.5 flex min-w-0 items-center justify-between gap-2">
              <span className="min-w-0 truncate text-[11px] text-muted-foreground">
                {task.targetLabel ? `${task.targetLabel} · ` : ""}
                {localizedCourseNextAction}
              </span>
              {activeSurface !== task.surface ? (
                <button
                  type="button"
                  onClick={() => void openSurface()}
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground hover:bg-muted"
                >
                  <ExternalLink className="h-3 w-3" aria-hidden />
                  {task.surfaceLabel}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {visibleApprovalOutcome ? (
          <div
            ref={approvalOutcomeRef}
            tabIndex={-1}
            role={visibleApprovalOutcome.kind === "error" ? "alert" : "status"}
            aria-live={
              visibleApprovalOutcome.kind === "error" ? "assertive" : "polite"
            }
            className={`mt-3 rounded-md border px-3 py-2 text-xs leading-5 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:ring-offset-2 focus:ring-offset-background ${
              visibleApprovalOutcome.kind === "error"
                ? "border-destructive/30 bg-destructive/5 text-destructive"
                : "border-border bg-background text-foreground"
            }`}
            data-testid="studio-command-deck-approval-outcome"
          >
            {visibleApprovalOutcome.message}
          </div>
        ) : null}

        {commandDeck.impactAvailable || pendingForApproval ? (
          <div
            className="mt-3 border-t border-border/70 pt-3"
            data-testid="studio-command-deck-impact"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {executionCopy.commandDeck.affectedOutputs}
              </span>
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {executionCopy.commandDeck.changes(commandDeck.changeCount)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {commandDeck.affectedArtifacts.map((artifact) => (
                <span
                  key={artifact.domain}
                  className="inline-flex min-h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground"
                  data-artifact-impact={artifact.domain}
                >
                  {artifact.approvalPending ? (
                    <Clock3
                      className="h-3 w-3 text-muted-foreground"
                      aria-hidden
                    />
                  ) : (
                    <CheckCircle2
                      className="h-3 w-3 text-muted-foreground"
                      aria-hidden
                    />
                  )}
                  {artifactLabel(artifact.domain)}
                  <span className="tabular-nums text-muted-foreground">
                    {artifact.changeCount}
                  </span>
                </span>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setImpactOpen((open) => !open);
              }}
              aria-expanded={impactOpen}
              aria-controls="studio-command-deck-impact-review"
              className={`mt-2 flex h-11 w-full touch-manipulation items-center justify-between gap-3 rounded-md px-3 text-left text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none ${
                commandDeck.approvalRequired
                  ? "bg-foreground text-background hover:bg-foreground/90"
                  : "border border-border bg-background text-foreground hover:bg-muted"
              }`}
            >
              <span>
                {impactOpen
                  ? executionCopy.commandDeck.hideImpact
                  : commandDeck.approvalRequired
                    ? executionCopy.commandDeck.reviewAndApprove
                    : executionCopy.commandDeck.reviewImpact}
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 transition-transform motion-reduce:transition-none ${
                  impactOpen ? "rotate-180" : ""
                }`}
                aria-hidden
              />
            </button>

            {impactOpen ? (
              <div
                id="studio-command-deck-impact-review"
                className="mt-2 rounded-md border border-border bg-background p-3"
              >
                {pendingForApproval ? (
                  <>
                    <p className="text-xs font-semibold text-foreground">
                      {executionCopy.commandDeck.preparedImpactSummary(
                        commandDeck.changeCount,
                      )}
                    </p>
                    <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                      {executionCopy.commandDeck.coordinatedApproval}
                    </p>
                  </>
                ) : null}
                {impactChanges.length > 0 ? (
                  <div className="mt-2 max-h-64 space-y-2 overflow-y-auto pr-1">
                    {impactChanges.map((change, index) => (
                      <div
                        key={change.path}
                        className="border-t border-border/70 pt-2 first:border-t-0 first:pt-0"
                        data-testid="studio-command-deck-impact-change"
                      >
                        <p className="text-[11px] font-medium text-foreground [overflow-wrap:anywhere]">
                          {executionCopy.commandDeck.changeItem(index + 1)}
                        </p>
                        <code className="mt-0.5 block text-[10px] text-muted-foreground [overflow-wrap:anywhere]">
                          {change.path}
                        </code>
                        {"before" in change || "proposed" in change ? (
                          <dl className="mt-1 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-[10px] text-muted-foreground">
                            {"before" in change &&
                            change.before !== undefined ? (
                              <>
                                <dt>{executionCopy.commandDeck.before}</dt>
                                <dd className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]">
                                  {formatImpactValue(change.before)}
                                </dd>
                              </>
                            ) : null}
                            {"proposed" in change &&
                            change.proposed !== undefined ? (
                              <>
                                <dt>{executionCopy.commandDeck.after}</dt>
                                <dd className="min-w-0 whitespace-pre-wrap text-foreground [overflow-wrap:anywhere]">
                                  {formatImpactValue(change.proposed)}
                                </dd>
                              </>
                            ) : null}
                          </dl>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                    <p>{executionCopy.commandDeck.noImpact}</p>
                    {pendingForApproval ? (
                      <p className="text-destructive" role="alert">
                        {executionCopy.commandDeck.approvalBlockedNoEvidence}
                      </p>
                    ) : null}
                  </div>
                )}

                {commandDeck.approvalRequired &&
                pendingForApproval?.impact.confirmation === "typed" ? (
                  <label className="mt-3 block text-xs font-medium text-foreground">
                    {executionCopy.commandDeck.typeToConfirmHint}
                    <code
                      data-testid="command-deck-confirm-phrase"
                      className="mt-1.5 block select-all rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm text-foreground [overflow-wrap:anywhere]"
                    >
                      {pendingForApproval.impact.confirmationPhrase ?? ""}
                    </code>
                    <input
                      type="text"
                      value={confirmationText}
                      onChange={(event) =>
                        setConfirmationText(event.target.value)
                      }
                      className="mt-1.5 h-11 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </label>
                ) : null}

                {pendingForApproval ? (
                  <div
                    className={`mt-3 grid gap-2 ${
                      commandDeck.approvalRequired
                        ? "grid-cols-2"
                        : "grid-cols-1"
                    }`}
                  >
                    {commandDeck.approvalRequired ? (
                      <button
                        type="button"
                        onClick={() => void approvePendingPlan()}
                        disabled={
                          approvalState !== "idle" || !typedApprovalReady
                        }
                        className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-semibold text-background hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                        {approvalState === "applying"
                          ? executionCopy.commandDeck.applying
                          : executionCopy.commandDeck.approve}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => void cancelPendingPlan()}
                      disabled={approvalState !== "idle"}
                      className="inline-flex h-11 items-center justify-center rounded-md border border-border px-3 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {approvalState === "cancelling"
                        ? executionCopy.commandDeck.cancelling
                        : executionCopy.commandDeck.cancel}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="min-w-0">
          {task.execution ? (
            <div className="mt-2 border-t border-border/60 pt-2">
              <button
                type="button"
                onClick={() => {
                  setExecutionDetailsOpen((open) => !open);
                  setUndoError(null);
                }}
                aria-expanded={executionDetailsOpen}
                aria-controls={executionDetailsId}
                className="flex h-11 w-full touch-manipulation items-center justify-between gap-3 rounded-md px-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
              >
                <span>
                  {executionCopy.taskCenter.latestExecution(
                    task.execution.events.length,
                  )}
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${
                    executionDetailsOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden
                />
              </button>
              {executionDetailsOpen ? (
                <div id={executionDetailsId}>
                  <Suspense
                    fallback={
                      <p
                        className="mt-2 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground"
                        aria-live="polite"
                      >
                        {executionCopy.taskCenter.loadingDetails}
                      </p>
                    }
                  >
                    <LazyStudioExecutionTimeline
                      key={task.execution.executionId}
                      execution={task.execution}
                      isUndoing={isUndoing}
                      undoError={undoError}
                      onUndo={() => void undoExecution()}
                    />
                  </Suspense>
                </div>
              ) : null}
            </div>
          ) : null}
          {journey ? <GoldenJourneyProgress journey={journey} locale={locale} /> : null}
        </div>
      </div>
    </section>
  );
}

function GoldenJourneyProgress({ journey, locale }: { journey: StudioGoldenJourneySnapshot; locale: LocaleCode }) {
  const copy =
    locale === 'ko'
      ? {
          continue: '계속',
          continueLabel: (label: string) => `${label} 계속하기`,
          clearLabel: (label: string) => `${label} 가이드 지우기`,
          clearTitle: '여정 가이드 지우기',
          progress: '여정 진행률',
          complete: '모든 단계가 완료되었습니다. 릴리스 전에 보존된 증거를 검토하세요.',
        }
      : locale === 'ja'
        ? {
            continue: '続ける',
            continueLabel: (label: string) => `${label}を続ける`,
            clearLabel: (label: string) => `${label}ガイドを消去`,
            clearTitle: 'ジャーニーガイドを消去',
            progress: 'ジャーニーの進行状況',
            complete: 'すべての手順が完了しました。リリース前に保存された証拠を確認してください。',
          }
        : locale === 'zh'
          ? {
              continue: '继续',
              continueLabel: (label: string) => `继续 ${label}`,
              clearLabel: (label: string) => `清除 ${label} 指引`,
              clearTitle: '清除流程指引',
              progress: '流程进度',
              complete: '所有步骤已完成。发布前请检查留存的证据。',
            }
          : locale === 'zh-TW'
            ? {
                continue: '繼續',
                continueLabel: (label: string) => `繼續 ${label}`,
                clearLabel: (label: string) => `清除 ${label} 指引`,
                clearTitle: '清除流程指引',
                progress: '流程進度',
                complete: '所有步驟已完成。發佈前請檢查保留的證據。',
              }
            : {
                continue: 'Continue',
                continueLabel: (label: string) => `Continue ${label}`,
                clearLabel: (label: string) => `Clear ${label}`,
                clearTitle: 'Clear journey guide',
                progress: 'Journey progress',
                complete: 'All steps are complete. Review retained evidence before release.',
              }
  const continueJourney = () => {
    activateStudioGoldenJourneyStep(journey);
  };

  return (
    <div
      className="mt-3 border-t border-border/70 pt-3"
      data-testid="studio-golden-journey"
      data-journey-id={journey.definition.id}
      data-journey-status={journey.status}
    >
      <div className="flex min-w-0 items-center gap-2">
        <ListChecks className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-foreground">
          {journey.definition.label}
        </span>
        <span className="text-[10px] tabular-nums text-muted-foreground">
          {journey.completedStepCount}/{journey.totalStepCount}
        </span>
        {journey.currentStep ? (
          <button
            type="button"
            onClick={continueJourney}
            aria-label={copy.continueLabel(journey.definition.label)}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[10px] font-medium text-foreground hover:bg-muted"
          >
            <Play className="h-3 w-3" aria-hidden />
            {copy.continue}
          </button>
        ) : null}
        <button
          type="button"
          onClick={clearStudioGoldenJourney}
          aria-label={copy.clearLabel(journey.definition.label)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          title={copy.clearTitle}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <div className="mt-2 grid grid-flow-col auto-cols-fr gap-1" aria-label={copy.progress}>
        {journey.steps.map((step) => (
          <span
            key={step.id}
            className={`h-1 rounded-sm ${
              step.status === "completed"
                ? "bg-foreground"
                : step.status === "failed"
                  ? "bg-destructive"
                  : step.status === "awaiting-confirmation"
                    ? "bg-amber-500"
                    : step.status === "active"
                      ? "bg-primary"
                      : "bg-border"
            }`}
            title={`${step.label}: ${step.status}`}
          />
        ))}
      </div>
      <p className="mt-2 text-[11px] leading-4 text-muted-foreground">
        {journey.currentStep ? `${journey.currentStep.label}: ${journey.currentStep.instruction}` : copy.complete}
      </p>
    </div>
  );
}
